import { useCallback, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import { differenceInCalendarDays, parseISO } from "date-fns";
import { Trash2Icon } from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { DateInput } from "simplydo/react/ui/date";
import { Divider } from "simplydo/react/ui/divider";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { H4, H5, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Loader } from "simplydo/react/ui/loader";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";

import api from "api";
import util from "utils/utils";

import GetCalendarLinkButton from "components/calendars/Calendar/GetCalendarLinkButton";

type CalendarSettingsProps = {
  forType: "group" | "challenge";
  forId: string;
  calendarId: string;
};

type TargetUsers = "members" | "calendarAccess";

function getEarliestEventYmd(
  events: Array<{ from?: string; isGenerated?: boolean; eventType?: string }>,
): string | null {
  const dates = events
    .filter(
      (event) =>
        event.from && !event.isGenerated && event.eventType !== "background",
    )
    .map((event) => new Date(event.from!).toISOString().split("T")[0])
    .sort();
  return dates[0] ?? null;
}

type CalendarMoveWholeSectionProps = {
  calendarId: string;
  referenceStartYmd: string;
  invalidateCalendar: () => void;
};

const CalendarMoveWholeSection = ({
  calendarId,
  referenceStartYmd,
  invalidateCalendar,
}: CalendarMoveWholeSectionProps) => {
  const [proposedStartYmd, setProposedStartYmd] = useState(referenceStartYmd);

  const shiftDiffDays = useMemo(
    () =>
      differenceInCalendarDays(
        parseISO(proposedStartYmd),
        parseISO(referenceStartYmd),
      ),
    [referenceStartYmd, proposedStartYmd],
  );

  const shiftCalendar = useCallback(() => {
    if (shiftDiffDays === 0) {
      return;
    }
    api.calendars.shiftCalendarEvents(
      calendarId,
      { shiftDays: Math.abs(shiftDiffDays), shiftForward: shiftDiffDays > 0 },
      () => {
        toast.success("Calendar moved successfully");
        invalidateCalendar();
      },
      (error) => {
        toast.error(error.message);
      },
    );
  }, [shiftDiffDays, calendarId, invalidateCalendar]);

  return (
    <>
      <H4>
        Move whole calendar
        <Subheader>
          Pick a new reference date. All events and milestones move by the same
          number of days so the schedule stays the same, just on different
          dates.
        </Subheader>
      </H4>
      <FormField>
        <FormLabel htmlFor="calendarShiftStartDate">
          New reference date
        </FormLabel>
        <DateInput
          clearable
          id="calendarShiftStartDate"
          placeholder="Select a new reference date"
          value={proposedStartYmd}
          dateFormat="yyyy-MM-dd"
          onChange={(_e, { value }) => setProposedStartYmd(String(value ?? ""))}
        />
      </FormField>
      {shiftDiffDays ? (
        <Message style={{ marginTop: 12 }}>
          <MessageContent>
            {shiftDiffDays > 0
              ? `Everything moves forward by ${util.pluralise(shiftDiffDays, "day", "days")} (reference becomes ${formatDate(parseISO(proposedStartYmd), "dd/MM/yyyy")}).`
              : `Everything moves back by ${util.pluralise(Math.abs(shiftDiffDays), "day", "days")} (reference becomes ${formatDate(parseISO(proposedStartYmd), "dd/MM/yyyy")}).`}
            <Divider />
            <Button onClick={shiftCalendar} color="primary">
              Apply move
            </Button>
          </MessageContent>
        </Message>
      ) : null}
    </>
  );
};

const CalendarSettings = ({
  forType,
  forId,
  calendarId,
}: CalendarSettingsProps) => {
  const navigate = useNavigate();

  const calendarQuery = useQuery(
    initUseQuery(api.calendars.getCalendar, [calendarId], {
      isEnabled: !!calendarId,
    }),
  );

  const calendar = calendarQuery.data?.calendar;
  const targetUsers = (calendar?.targetUsers || "members") as TargetUsers;

  const referenceStartYmd = useMemo(() => {
    const fromEvents = getEarliestEventYmd(calendar?.events || []);
    if (fromEvents) {
      return fromEvents;
    }
    if (calendar?.start) {
      return new Date(calendar.start).toISOString().split("T")[0]!;
    }
    return new Date().toISOString().split("T")[0]!;
  }, [calendar]);

  const invalidateCalendar = useCallback(() => {
    api.invalidateQueries(api.calendars.getCalendar, [calendarId]);
  }, [calendarId]);

  const setTargetUsers = useCallback(
    (value: TargetUsers) => {
      api.calendars.update(
        calendarId,
        { targetUsers: value },
        () => {
          invalidateCalendar();
          api.invalidateQueries(api.calendars.get, [calendarId]);
          api.invalidateQueries(api.calendars.getAll, [forType, forId]);
        },
        (error) => {
          toast.error(error.message);
        },
      );
    },
    [calendarId, forId, forType, invalidateCalendar],
  );

  const deleteCalendar = useCallback(() => {
    util
      .confirm(
        "Delete calendar?",
        "Deleting this calendar removes it for everyone in the group, including all scheduled events. This action cannot be undone. Are you sure?",
      )
      .then(() => {
        let navigateTo = "";
        if (forType === "group") {
          navigateTo = `/groups/${forId}/settings/calendars`;
        }
        api.calendars.delete(
          calendarId,
          () => {
            api.invalidateQueries(api.calendars.getAll, [forType, forId]);
            api.invalidateQueries(api.calendars.get, [calendarId]);
            api.invalidateQueries(api.calendars.getCalendar, [calendarId]);
            if (navigateTo) {
              navigate(navigateTo);
            }
          },
          (err) => toast.error(err.message),
        );
      })
      .catch(() => {});
  }, [calendarId, forType, forId, navigate]);

  if (calendarQuery.isLoading) {
    return (
      <Card fluid>
        <CardContent>
          <Loader />
        </CardContent>
      </Card>
    );
  }

  if (calendarQuery.isError) {
    return (
      <Card fluid>
        <CardContent>
          <Message color="red">
            Could not load calendar settings. You may need calendar edit access.
          </Message>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card fluid style={{ marginTop: 0 }}>
      <CardContent>
        {forType === "challenge" ? (
          <>
            <CalendarMoveWholeSection
              key={`${calendarId}-${referenceStartYmd}`}
              calendarId={calendarId}
              referenceStartYmd={referenceStartYmd}
              invalidateCalendar={invalidateCalendar}
            />
          </>
        ) : null}
        <H4>
          Sync with your calendar
          <Subheader>
            Subscribe in Google Calendar, Apple Calendar, Outlook, or other
            apps. Updates to this calendar will appear in your app.
          </Subheader>
        </H4>
        <GetCalendarLinkButton calendarId={calendarId} />

        {forType === "group" ? (
          <>
            <Divider />
            <H4>Delete calendar</H4>
            <p style={{ marginBottom: 10 }}>
              Deleting this calendar removes it for everyone, including all
              scheduled events. This action cannot be undone.
            </p>
            <Button
              onClick={() => deleteCalendar()}
              variant="outline"
              color="red"
            >
              <Icon icon={Trash2Icon} />
              Delete
            </Button>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default CalendarSettings;
